const USER_KEY = 'user';
const TOKEN_KEY = 'token';

const getStorage = (remember) => (remember ? localStorage : sessionStorage);

const readJson = (value) => {
  if (!value || value === 'undefined') {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const normalizeToken = (token) => {
  if (typeof token !== 'string') {
    return '';
  }

  return token.trim();
};

const decodeJwtPayload = (token) => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(window.atob(padded));
  } catch (error) {
    return null;
  }
};

export const isTokenValid = (token) => {
  const normalizedToken = normalizeToken(token);
  if (!normalizedToken) {
    return false;
  }

  const payload = decodeJwtPayload(normalizedToken);
  if (!payload?.exp) {
    return true;
  }

  return payload.exp * 1000 > Date.now();
};

export const clearStoredAuth = () => {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
};

export const createSafeAuthData = (data) => ({
  ...(data && typeof data === 'object' ? data : {}),
  access_token: normalizeToken(data?.access_token),
});

export const persistAuth = (data, remember = false) => {
  const safeData = createSafeAuthData(data);
  const token = safeData.access_token;

  clearStoredAuth();

  if (!token) {
    return;
  }

  const storage = getStorage(Boolean(remember));
  storage.setItem(USER_KEY, JSON.stringify(safeData));
  storage.setItem(TOKEN_KEY, token);
};

export const getStoredAuth = () => {
  const sources = [localStorage, sessionStorage];

  for (const storage of sources) {
    const token = normalizeToken(storage.getItem(TOKEN_KEY));
    const user = readJson(storage.getItem(USER_KEY));

    if (!token && !user) {
      continue;
    }

    if (!user || !isTokenValid(token)) {
      storage.removeItem(USER_KEY);
      storage.removeItem(TOKEN_KEY);
      continue;
    }

    return {
      storage,
      token,
      user: {
        ...user,
        access_token: token,
      },
    };
  }

  return {
    storage: null,
    token: '',
    user: null,
  };
};

export const hasStoredAuth = () => {
  const storedAuth = getStoredAuth();
  return Boolean(storedAuth.user && storedAuth.token);
};
